/**
 * Which anatomy a rig's part name belongs to.
 *
 * Derived rather than enumerated because R6 and R15 name the same anatomy
 * differently — `Torso` vs `UpperTorso`/`LowerTorso`, `LeftArm` vs
 * `LeftUpperArm`/`LeftLowerArm`/`LeftHand`. A lookup table per rig is a table to
 * forget when a body is added; these substrings hold for every Roblox rig.
 *
 * Shared rather than living in the viewer, because the export needs the same
 * answer and must not reach three.js to get it. Two copies of this rule would
 * be two things to keep in step, and the failure would be an exported file that
 * disagrees with the preview about which parts a garment covers — the exact
 * class of bug this whole feature has been fighting.
 */

export type PartGroup = "torso" | "arms" | "legs";

export function partGroup(name: string): PartGroup | "skin" {
  if (name.includes("Torso")) return "torso";
  if (name.includes("Arm") || name.includes("Hand")) return "arms";
  if (name.includes("Leg") || name.includes("Foot")) return "legs";
  return "skin"; // Head, and anything a future body adds that clothing misses
}

/** Which side of the body a part is on, or null for the ones down the middle. */
export type BodySide = "left" | "right";

/**
 * Left or right, for the parts that have a side.
 *
 * `partGroup` deliberately puts both arms in one "arms" group, which is right
 * for deciding what a *garment* covers and wrong for deciding where a *stroke*
 * may go. Keeping a stroke on one body part was checked with the group alone,
 * so both arms compared equal and paint carried straight across the body onto
 * the opposite limb — with "Paint across joints" switched off, which is the one
 * setting that exists to stop it. The mirrored limb is also the least likely
 * place to notice: it looks like a symmetry feature nobody asked for.
 *
 * Matched case-insensitively on the whole name so it holds for both rigs: R6
 * says "Left Arm", R15 says "LeftUpperArm", "LeftLowerArm", "LeftHand". Torso
 * and Head carry no side and return null, so a torso stroke is unaffected.
 */
export function partSide(name: string): BodySide | null {
  const n = name.toLowerCase();
  if (n.includes("left")) return "left";
  if (n.includes("right")) return "right";
  return null;
}

/**
 * A shirt covers torso + arms; pants cover torso + legs.
 *
 * Legs reuse the same limb regions as arms, so a combined map would have them
 * writing to the same texels and the loser would be silently wrong.
 */
export const GARMENT_GROUPS: Record<"shirt" | "pants", PartGroup[]> = {
  shirt: ["torso", "arms"],
  pants: ["torso", "legs"],
};

/** The parts a garment covers, out of everything a rig ships. */
export function partsForGarment(names: readonly string[], garment: "shirt" | "pants"): string[] {
  const groups = GARMENT_GROUPS[garment];
  return names.filter((n) => {
    const g = partGroup(n);
    return g !== "skin" && groups.includes(g);
  });
}

/**
 * Whether a rig is segmented — an R15 body, whose limbs come in three parts.
 *
 * Read off the names for the same reason as everything else here. It decides
 * whether maps are sealed against banded regions or whole faces, and getting it
 * wrong is not visible until a seam is painted.
 */
export function isSegmented(names: readonly string[]): boolean {
  return names.some((n) => n.includes("Upper"));
}
